import { datadogRum } from '@datadog/browser-rum';
import NextRouter from 'next/router';
import type { ReactNode } from 'react';
import { useEffectOnce } from 'react-use';
import { InteractionType } from '@/common/types/interaction';
import {
  getInteractionInformation,
  getRouteFromPathname,
  trackClientGroup,
  trackPropertyInteraction,
  trackReferral,
} from './helpers';

export const UserTrackingProvider = ({ children }: { children: ReactNode }) => {
  useEffectOnce(() => {
    const trackLastViewed = async () => {
      if (!NextRouter.pathname) return;
      const { pathname } = NextRouter;

      try {
        const path = getRouteFromPathname(window.location.pathname);
        const { interactionId, interactionType } = getInteractionInformation();
        if (!interactionType || !interactionId) return;

        if (interactionType === InteractionType.clientGroup) {
          await trackClientGroup({ path, interactionId });
        } else if (interactionType === InteractionType.referral) {
          await trackReferral({ path, interactionId });
        } else {
          await trackPropertyInteraction({
            path,
            interactionId,
            pathname,
            interactionType,
          });
        }
      } catch (error) {
        datadogRum.addError(error, {
          message: 'Failed to update last viewed location',
        });
      }
    };

    const doTrackLastViewed = () => void trackLastViewed();

    NextRouter.events.on('routeChangeComplete', doTrackLastViewed);

    return () => {
      NextRouter.events.off('routeChangeComplete', doTrackLastViewed);
    };
  });

  return children;
};
